import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { PAGES } from '../lib/site';
import { getProfessionalUseEvidenceById } from '../lib/professionalUseEvidence';

const page = PAGES.find((p) => p.path === '/quem-pode-aplicar');
const satepsi = getProfessionalUseEvidenceById('satepsi-mini-mental-nonexclusive');
const cfp = getProfessionalUseEvidenceById('cfp-psychological-tests-exclusive-2026');

const ANSWER =
  'No Brasil, o TESTE MINI-MENTAL aparece atualmente no SATEPSI como instrumento não privativo do psicólogo. Isso significa que sua aplicação não está reservada exclusivamente à Psicologia, mas não significa uso irrestrito: em contexto assistencial, o uso depende de competência técnica, escopo profissional, finalidade e responsabilidade sobre o que é feito com o resultado.';

const REFERENCES = [
  `${satepsi.authority}. Instrumentos Não Privativos. Classificação atual: ${satepsi.results.instrument} — ${satepsi.results.classification}.`,
  `${cfp.authority}. CFP destaca que aplicação de testes psicológicos é prática privativa da Psicologia. 28/07/2026.`
];

const RELATED = [
  {
    to: '/como-aplicar',
    label: 'Como aplicar o Mini Mental',
    note: 'o que significa preservar a padronização e a comparabilidade.'
  },
  {
    to: '/erros-de-aplicacao',
    label: 'Erros comuns na aplicação',
    note: 'desvios que podem alterar a interpretação do escore.'
  },
  {
    to: '/interpretacao-dos-resultados',
    label: 'Como interpretar o resultado',
    note: 'por que a pontuação é apenas uma parte da avaliação.'
  },
  {
    to: '/diagnostica-alzheimer',
    label: 'O Mini Mental diagnostica Alzheimer?',
    note: 'a diferença entre rastrear e estabelecer diagnóstico.'
  },
  {
    to: '/formulario-e-direitos-de-uso',
    label: 'Formulário e direitos de uso',
    note: 'limites de reprodução e acesso ao instrumento.'
  }
];

export default function QuemPodeAplicar() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="privativo-psicologia">
        <h2 id="privativo-psicologia" className="text-2xl mb-5">O Mini-Mental é privativo de psicólogo?</h2>
        <p className="text-[var(--text-muted)] mb-4"><strong>Não, segundo a classificação atual do SATEPSI.</strong></p>
        <p className="text-[var(--text-muted)]">
          O sistema do Conselho Federal de Psicologia lista o TESTE MINI-MENTAL entre os
          <strong> instrumentos não privativos</strong>. Em termos regulatórios, o SATEPSI diferencia
          um <strong>instrumento não privativo</strong> de um <strong>teste psicológico</strong>, cuja
          aplicação integra o campo privativo da Psicologia.
        </p>
      </section>

      <section aria-labelledby="qualquer-pessoa">
        <h2 id="qualquer-pessoa" className="text-2xl mb-5">Então qualquer pessoa pode aplicar?</h2>
        <p className="text-[var(--text-muted)] mb-4"><strong>Também não.</strong></p>
        <p className="text-[var(--text-muted)]">
          A classificação como instrumento não privativo não equivale a uso irrestrito. Em contexto
          assistencial, continuam importando a capacitação técnica, a finalidade da avaliação, as
          atribuições legais da profissão e a responsabilidade sobre a interpretação e a conduta que
          decorrem do resultado.
        </p>
      </section>

      <section aria-labelledby="profissionais-saude">
        <h2 id="profissionais-saude" className="text-2xl mb-5">E médicos, enfermeiros e outros profissionais de saúde?</h2>
        <p className="text-[var(--text-muted)]">
          O ponto seguro não é construir uma lista universal de profissões autorizadas. Instrumentos
          não privativos podem ser incorporados à prática dentro das atribuições legais e técnicas de
          cada categoria e da finalidade assistencial concreta. Quando houver dúvida sobre emissão de
          documentos, diagnóstico ou outro ato profissional, a normativa vigente da própria profissão
          deve ser conferida.
        </p>
      </section>

      <section aria-labelledby="aplicar-diagnosticar">
        <h2 id="aplicar-diagnosticar" className="text-2xl mb-5">Aplicar não é diagnosticar</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Obter uma pontuação de rastreio é diferente de concluir diagnóstico, emitir laudo, indicar
          tratamento ou praticar outro ato regulado. O escore isolado não estabelece diagnóstico.
        </p>
        <p className="text-[var(--text-muted)]">
          A interpretação precisa respeitar o escopo profissional e o contexto clínico. Veja{' '}
          <Link to="/interpretacao-dos-resultados" className=" text-[var(--primary)] hover:underline">
            como interpretar o resultado
          </Link>{' '}
          e por que o{' '}
          <Link to="/diagnostica-alzheimer" className=" text-[var(--primary)] hover:underline">
            MEEM não diagnostica Alzheimer sozinho
          </Link>.
        </p>
      </section>

      <section aria-labelledby="por-que-confusao">
        <h2 id="por-que-confusao" className="text-2xl mb-5">Por que existe tanta confusão sobre isso?</h2>
        <p className="text-[var(--text-muted)]">
          Em 28 de julho de 2026, o CFP reafirmou que a aplicação de <strong>testes psicológicos</strong>
          é prática privativa da Psicologia. Isso não contradiz a situação do Mini-Mental: o próprio
          SATEPSI mantém uma categoria separada de <strong>instrumentos não privativos</strong>, na qual
          o TESTE MINI-MENTAL aparece atualmente.
        </p>
      </section>

      <section aria-labelledby="capacitacao">
        <h2 id="capacitacao" className="text-2xl mb-5">O que significa estar capacitado para aplicar?</h2>
        <ul className="space-y-3 text-[var(--text-muted)] list-disc pl-5">
          <li>usar uma versão definida e um método consistente;</li>
          <li>preservar a padronização da aplicação e da pontuação;</li>
          <li>reconhecer barreiras sensoriais, motoras ou clínicas que afetem o desempenho;</li>
          <li>documentar condições e desvios relevantes;</li>
          <li>saber quando o escore não pode ser interpretado ou comparado de forma confiável.</li>
        </ul>
        <p className="text-[var(--text-muted)] mt-4">
          Veja o processo em{' '}
          <Link to="/como-aplicar" className=" text-[var(--primary)] hover:underline">como aplicar o Mini Mental</Link>
          {' '}e os principais desvios em{' '}
          <Link to="/erros-de-aplicacao" className=" text-[var(--primary)] hover:underline">erros de aplicação</Link>.
        </p>
      </section>

      <section aria-labelledby="autoaplicacao">
        <h2 id="autoaplicacao" className="text-2xl mb-5">Autoaplicação e aplicação por familiares</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Um resultado obtido sem administração adequada não deve ser tratado como equivalente a uma
          aplicação clínica padronizada. Isso vale especialmente quando não se conhece a versão usada,
          as condições de aplicação ou os critérios de pontuação.
        </p>
        <p className="text-[var(--text-muted)]">
          Há ainda limites de reprodução do instrumento. Este site não publica os itens ou comandos
          literais do MMSE; veja{' '}
          <Link to="/formulario-e-direitos-de-uso" className=" text-[var(--primary)] hover:underline">
            formulário e direitos de uso
          </Link>.
        </p>
      </section>

      <section aria-labelledby="fluxo-final">
        <h2 id="fluxo-final" className="text-2xl mb-5">Fluxo prático</h2>
        <p className="text-[var(--text-muted)] font-medium">
          instrumento não privativo → competência da profissão → capacitação → aplicação adequada → interpretação dentro do escopo → encaminhamento quando necessário
        </p>
      </section>
    </ArticleLayout>
  );
}
